import { useCallback, useEffect, useState } from "react";
import {
  createResourceAllocationOrder,
  getResourceAllocationOrders,
  getResourceAllocationProjects,
} from "../services/resourceAllocation.service";
import type {
  IResourceAllocationOrderApi,
  IResourceAllocationProjectApi,
} from "../services/types/resourceAllocation";

export default function useResourceAllocationOrders(
  employeeId: string | undefined,
  year: number,
  month: number,
  projectId?: string
) {
  const [orders, setOrders] = useState<IResourceAllocationOrderApi[]>([]);
  const [projects, setProjects] = useState<IResourceAllocationProjectApi[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchProjects = useCallback(async () => {
    if (!employeeId) return;
    const data = await getResourceAllocationProjects(employeeId);
    setProjects(data);
  }, [employeeId]);

  const fetchOrders = useCallback(async () => {
    if (!employeeId || month < 1 || month > 12) return;

    setIsLoading(true);
    try {
      const data = await getResourceAllocationOrders(
        employeeId,
        year,
        month,
        projectId || undefined
      );
      setOrders(data);
    } finally {
      setIsLoading(false);
    }
  }, [employeeId, year, month, projectId]);

  const createOrder = async (payload: {
    assignmentDescription: string;
    durationMinutes: number;
    youTrackProjectId: string;
    youTrackProjectLabel: string;
  }) => {
    if (!employeeId) return;

    setIsSubmitting(true);
    try {
      await createResourceAllocationOrder(employeeId, {
        year,
        month,
        ...payload,
      });
      await fetchOrders();
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  return {
    orders,
    projects,
    isLoading,
    isSubmitting,
    refetchOrders: fetchOrders,
    createOrder,
  };
}
